import { Heading1Component } from "./Tags/heading";
import { testimonialDatas } from "../data";
import { useState, useEffect } from "react";

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 3;

  // Auto-rotate testimonials every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => 
        prev + itemsPerPage >= testimonialDatas.length ? 0 : prev + itemsPerPage
      );
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const goToPrevious = () => {
    setCurrentIndex((prev) => 
      prev - itemsPerPage < 0 ? Math.max(0, testimonialDatas.length - itemsPerPage) : prev - itemsPerPage
    );
  };

  const goToNext = () => {
    setCurrentIndex((prev) => 
      prev + itemsPerPage >= testimonialDatas.length ? 0 : prev + itemsPerPage
    );
  };

  const goToPage = (pageIndex: number) => {
    setCurrentIndex(pageIndex * itemsPerPage);
  };

  const totalPages = Math.ceil(testimonialDatas.length / itemsPerPage);
  const currentPage = Math.floor(currentIndex / itemsPerPage);
  const visibleTestimonials = testimonialDatas.slice(
    currentIndex,
    currentIndex + itemsPerPage
  );

  return (
    <section className="py-20 bg-gradient-to-b from-white to-green-50">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Section Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            Client Testimonials
          </div>
          <Heading1Component
            _style="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800"
            text="Hear From Our Happy Customers"
          />
          <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
            Discover why individuals and businesses trust GJC Recycling for their waste management needs.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {visibleTestimonials.map((data, index) => {
            const initials = data.name
              .split(' ')
              .map(word => word[0])
              .join('')
              .toUpperCase()
              .slice(0, 2);

            return (
              <div 
                key={index} 
                className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 h-full transform hover:-translate-y-1"
              >
                {/* Rating Stars */}
                <div className="flex text-yellow-400 text-sm mb-4">
                  <span>★</span>
                  <span>★</span>
                  <span>★</span>
                  <span>★</span>
                  <span>★</span>
                </div>
                
                {/* Content */}
                <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6 italic line-clamp-4">
                  "{data.content}"
                </p>
                
                {/* Author Info */}
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-gradient-to-br from-green-400 to-green-600 flex-shrink-0">
                    {data.img ? (
                      <img
                        className="w-full h-full object-cover"
                        src={data.img}
                        alt={data.name}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white font-bold text-lg">
                        {initials}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">
                      {data.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {data.country}
                    </p>
                  </div>
                </div>

                {/* Decorative quote */}
                <div className="absolute bottom-4 right-4 text-green-100 text-6xl font-serif opacity-20">
                  "
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex justify-center items-center gap-4 mt-10">
          <button
            onClick={goToPrevious}
            className="w-10 h-10 bg-white rounded-full shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center text-gray-600 hover:text-green-600 hover:scale-110"
            aria-label="Previous testimonials"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          {/* Dots */}
          <div className="flex gap-2">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToPage(index)}
                className={`transition-all duration-300 rounded-full ${
                  currentPage === index 
                    ? 'bg-green-500 w-6 h-2' 
                    : 'bg-gray-300 w-2 h-2 hover:bg-gray-400'
                }`}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
          
          <button
            onClick={goToNext}
            className="w-10 h-10 bg-white rounded-full shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center text-gray-600 hover:text-green-600 hover:scale-110"
            aria-label="Next testimonials"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        {/* Trust Badge */}
        <div className="text-center mt-10">
          <div className="inline-flex items-center gap-6 bg-white rounded-full shadow-md px-6 py-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl">⭐</span>
              <span className="text-sm font-semibold text-gray-700">4.9/5</span>
            </div>
            <div className="w-px h-6 bg-gray-200"></div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Based on</span>
              <span className="text-sm font-semibold text-gray-700">{testimonialDatas.length}+</span>
              <span className="text-sm text-gray-500">reviews</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;